import { useRef, useState } from 'react';
import { useDrag, useDrop } from 'react-dnd';
import { useTranslation } from 'react-i18next';
import { withIcon } from '../../../../core/components/Icon.tsx';
import { faCheck, faGripDotsVertical, faPlus, faTrash } from '@fortawesome/pro-regular-svg-icons';
import classnames from '../../../../core/utils/classnames.tsx';
import { useOutputMapperEditor } from '../OutputMapperEditorProvider.tsx';
import AddFieldDialog from '../components/AddFieldDialog.tsx';

const CheckIcon = withIcon(faCheck);
const PlusIcon = withIcon(faPlus);
const TrashIcon = withIcon(faTrash);
const DragIcon = withIcon(faGripDotsVertical);

const DRAG_TYPE = 'TARGET_FIELD';

interface TargetFieldRowProps {
    target: string;
    actionCount: number;
    index: number;
    isSelected: boolean;
    onSelect: (index: number) => void;
    onDelete: (index: number) => void;
    onMove: (fromIndex: number, toIndex: number) => void;
}

const TargetFieldRow = ({
    target,
    actionCount,
    index,
    isSelected,
    onSelect,
    onDelete,
    onMove,
}: TargetFieldRowProps) => {
    const { t } = useTranslation('integrations');
    const dragRef = useRef<HTMLDivElement>(null);
    const previewRef = useRef<HTMLDivElement>(null);

    const [{ isDragging }, drag, dragPreview] = useDrag({
        type: DRAG_TYPE,
        item: { index },
        collect: (monitor) => ({ isDragging: monitor.isDragging() }),
    });

    const [, drop] = useDrop({
        accept: DRAG_TYPE,
        hover(item: { index: number }, monitor) {
            if (!previewRef.current) return;
            const dragIndex = item.index;
            const hoverIndex = index;
            if (dragIndex === hoverIndex) return;

            const rect = previewRef.current.getBoundingClientRect();
            const middleY = (rect.bottom - rect.top) / 2;
            const clientOffset = monitor.getClientOffset();
            if (!clientOffset) return;
            const hoverClientY = clientOffset.y - rect.top;

            if (dragIndex < hoverIndex && hoverClientY < middleY) return;
            if (dragIndex > hoverIndex && hoverClientY > middleY) return;

            onMove(dragIndex, hoverIndex);
            item.index = hoverIndex;
        },
    });

    drag(dragRef);
    drop(dragPreview(previewRef));

    return (
        <div
            ref={previewRef}
            className={classnames(
                'flex items-center gap-2 py-2 px-3 rounded cursor-pointer text-sm transition-colors group',
                'hover:bg-surface-medium',
                isDragging && 'opacity-50',
                isSelected &&
                    'bg-surface-brand-medium-default text-content-brand-strong hover:bg-surface-brand-medium-hover'
            )}
            onClick={() => onSelect(index)}
        >
            <div
                ref={dragRef}
                onClick={(e) => e.stopPropagation()}
                className={classnames(
                    'flex-none cursor-grab opacity-0 group-hover:opacity-100 transition-opacity',
                    isSelected ? 'text-content-brand-strong/70' : 'text-content-weak hover:text-content-strong'
                )}
                title={t('outputMapper.visualEditor.dragToReorder')}
            >
                <DragIcon className="w-3 h-3" />
            </div>
            <CheckIcon
                className={classnames(
                    'w-3 h-3 flex-none',
                    isSelected ? 'text-content-brand-strong' : 'text-status-success-medium'
                )}
            />
            <span className="flex-1 truncate font-medium">{target}</span>
            <span className={classnames('text-xs', isSelected ? 'text-content-brand-strong/70' : 'text-content-weak')}>
                {t('outputMapper.visualEditor.actionCount', { count: actionCount })}
            </span>
            <button
                type="button"
                onClick={(e) => {
                    e.stopPropagation();
                    onDelete(index);
                }}
                className={classnames(
                    'w-5 h-5 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-all rounded',
                    isSelected
                        ? 'text-content-brand-strong/70 hover:text-status-danger-medium hover:bg-surface-brand-medium-hover'
                        : 'text-content-weak hover:text-status-danger-medium hover:bg-surface-medium'
                )}
                title={t('outputMapper.visualEditor.deleteMapping')}
            >
                <TrashIcon className="w-3 h-3" />
            </button>
        </div>
    );
};

const TargetFieldsPanel = () => {
    const { t } = useTranslation('integrations');
    const { config, selectedMappingIndex, setSelectedMappingIndex, deleteMapping, moveMapping } =
        useOutputMapperEditor();

    const [showAddFieldDialog, setShowAddFieldDialog] = useState(false);

    return (
        <div className="h-full flex flex-col">
            <div className="flex items-center justify-between px-4 py-3 border-b border-line-weak flex-none">
                <h3 className="font-medium text-sm text-content-strong">
                    {t('outputMapper.visualEditor.targetFields')}
                </h3>
                <button
                    type="button"
                    onClick={() => setShowAddFieldDialog(true)}
                    className="flex items-center gap-1 text-xs text-content-brand-strong hover:text-content-brand-strong/80 transition-colors"
                >
                    <PlusIcon className="w-3 h-3" />
                    {t('outputMapper.visualEditor.addField')}
                </button>
            </div>

            {showAddFieldDialog && <AddFieldDialog mode="output" onClose={() => setShowAddFieldDialog(false)} />}

            <div className="flex-1 overflow-auto px-2 py-2">
                {config.output_mapping.length > 0 ? (
                    <div className="flex flex-col gap-1">
                        {config.output_mapping.map((mapping, index) => (
                            <TargetFieldRow
                                key={mapping.target}
                                target={mapping.target}
                                actionCount={mapping.actions.length}
                                index={index}
                                isSelected={selectedMappingIndex === index}
                                onSelect={setSelectedMappingIndex}
                                onDelete={deleteMapping}
                                onMove={moveMapping}
                            />
                        ))}
                    </div>
                ) : (
                    <div className="text-sm text-content-weak text-center py-8">
                        <div className="mb-2">{t('outputMapper.visualEditor.noMappings')}</div>
                        <button
                            type="button"
                            onClick={() => setShowAddFieldDialog(true)}
                            className="inline-flex items-center gap-1 text-content-brand-strong hover:text-content-brand-strong/80 transition-colors"
                        >
                            <PlusIcon className="w-3 h-3" />
                            {t('outputMapper.visualEditor.addFirstField')}
                        </button>
                    </div>
                )}
            </div>
        </div>
    );
};

export default TargetFieldsPanel;
